import React, { useState, useEffect, useRef } from 'react';
import { X, Maximize2, Minimize2, Camera, Clock, Trash2, Share2, Navigation as NavigationIcon, MapPin, Compass, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { hasBrowserGeolocation, hasNativeLocation, requestCurrentLocation } from '../utils/nativeCapabilities';
import '../styles/components/parking-assistant.css';

const ParkingAssistant = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [parkingData, setParkingData] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [notes, setNotes] = useState('');
  const [photo, setPhoto] = useState(null);
  const [elapsedTime, setElapsedTime] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [geoError, setGeoError] = useState(null);
  const [heading, setHeading] = useState(null);
  const [compassSupported, setCompassSupported] = useState(false);
  const [showCompass, setShowCompass] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [tempPosition, setTempPosition] = useState(null);
  const [isEditingPosition, setIsEditingPosition] = useState(false);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const mapRef = useRef(null);
  const fileInputRef = useRef(null);
  const watchIdRef = useRef(null);
  const lastPositionRef = useRef(null);
  const mapInitializedRef = useRef(false);
  const orientationHandlerRef = useRef(null);

  useEffect(() => {
    const saved = localStorage.getItem('parkingData');
    if (saved) {
      const data = JSON.parse(saved);
      setParkingData(data);
      setNotes(data.notes || '');
      setPhoto(data.photo || null);
    }

    if (window.DeviceOrientationEvent) {
      setCompassSupported(true);
    }
  }, []);

  // Calcule la distance entre deux positions en mètres
  const calculateDistanceBetweenPositions = (pos1, pos2) => {
    const R = 6371000; // Rayon de la Terre en mètres
    const dLat = (pos2.lat - pos1.lat) * Math.PI / 180;
    const dLon = (pos2.lng - pos1.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(pos1.lat * Math.PI / 180) * Math.cos(pos2.lat * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const requestGeolocation = () => {
    if (!hasNativeLocation() && !hasBrowserGeolocation()) {
      setGeoError(t('parking.errors.notSupported'));
      return;
    }

    try {
      requestCurrentLocation({ enableHighAccuracy: true, timeout: 10000, maximumAge: 0 })
        .then(
        (position) => {
          const newPos = {
            lat: position.latitude,
            lng: position.longitude
          };
          setCurrentLocation(newPos);
          lastPositionRef.current = newPos;
          setGeoError(null);

          // Seuil minimum de 10 mètres pour éviter les mises à jour trop fréquentes
          const DISTANCE_THRESHOLD = 10; // mètres

          if (!hasBrowserGeolocation() || !navigator.geolocation.watchPosition) {
            return;
          }

          watchIdRef.current = navigator.geolocation.watchPosition(
            (pos) => {
              const updatedPos = {
                lat: pos.coords.latitude,
                lng: pos.coords.longitude
              };
              
              // Ne mettre à jour que si le déplacement est significatif
              if (!lastPositionRef.current || 
                  calculateDistanceBetweenPositions(lastPositionRef.current, updatedPos) > DISTANCE_THRESHOLD) {
                setCurrentLocation(updatedPos);
                lastPositionRef.current = updatedPos;
              }
            },
            (error) => console.error('Erreur suivi position:', error),
            { enableHighAccuracy: true, maximumAge: 10000, timeout: 5000 }
          );
        },
        (error) => {
          console.error('Erreur géolocalisation:', error);
          if (error.code === 1) {
            setGeoError(t('parking.errors.permissionDenied'));
          } else if (error.code === 2) {
            setGeoError(t('parking.errors.unavailable'));
          } else if (error.code === 3) {
            setGeoError(t('parking.errors.timeout'));
          } else {
            setGeoError(t('parking.errors.general'));
          }
        }
      );
    } catch {
      setGeoError(t('parking.errors.general'));
    }
  };

  const startCompass = async () => {
    if (!compassSupported) return;

    try {
      // eslint-disable-next-line no-undef
      if (typeof DeviceOrientationEvent.requestPermission === 'function') {
        // eslint-disable-next-line no-undef
        const permission = await DeviceOrientationEvent.requestPermission();
        if (permission !== 'granted') {
          window.alert(t('parking.compass.permissionDenied'));
          return;
        }
      }

      const handleOrientation = (event) => {
        let compass = event.webkitCompassHeading || event.alpha;
        if (event.webkitCompassHeading) {
          compass = event.webkitCompassHeading;
        } else if (event.alpha) {
          compass = 360 - event.alpha;
        }
        setHeading(compass);
      };

      // Stocker le handler pour pouvoir le supprimer plus tard
      orientationHandlerRef.current = handleOrientation;
      window.addEventListener('deviceorientation', handleOrientation);
      setShowCompass(true);
    } catch (error) {
      console.error('Erreur compas:', error);
      window.alert(t('parking.compass.error'));
    }
  };

  const stopCompass = () => {
    if (orientationHandlerRef.current) {
      window.removeEventListener('deviceorientation', orientationHandlerRef.current);
      orientationHandlerRef.current = null;
    }
    setShowCompass(false);
    setHeading(null);
  };

  const calculateBearing = () => {
    if (!parkingData || !currentLocation) return 0;

    const lat1 = currentLocation.lat * Math.PI / 180;
    const lat2 = parkingData.lat * Math.PI / 180;
    const dLon = (parkingData.lng - currentLocation.lng) * Math.PI / 180;

    const y = Math.sin(dLon) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
    const bearing = Math.atan2(y, x) * 180 / Math.PI;

    return (bearing + 360) % 360;
  };

  const getCompassRotation = () => {
    const targetBearing = calculateBearing();
    if (heading !== null) {
      return targetBearing - heading;
    }
    return 0;
  };

  useEffect(() => {
    return () => {
      if (watchIdRef.current) {
        navigator.geolocation?.clearWatch?.(watchIdRef.current);
      }
      // Nettoyer le listener de la boussole
      if (orientationHandlerRef.current) {
        window.removeEventListener('deviceorientation', orientationHandlerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (parkingData) {
      const interval = setInterval(() => {
        const now = Date.now();
        const diff = now - parkingData.timestamp;
        const hours = Math.floor(diff / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        setElapsedTime(`${hours}h ${minutes}min`);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [parkingData]);

  const initMap = React.useCallback(() => {
    if (!mapRef.current) return;

    setIsMapLoading(true);

    const bounds = [];
    let mapHTML = '<div style="width:100%;height:100%;position:relative;background:linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));">';
    
    const displayPosition = tempPosition || currentLocation;
    
    if (parkingData) bounds.push([parkingData.lat, parkingData.lng]);
    if (displayPosition) bounds.push([displayPosition.lat, displayPosition.lng]);

    const center = bounds.length > 0
      ? [(bounds[0][0] + (bounds[1]?.[0] || bounds[0][0])) / 2,
         (bounds[0][1] + (bounds[1]?.[1] || bounds[0][1])) / 2]
      : [48.8566, 2.3522];

    const isNewParking = !parkingData && !!displayPosition;
    
    // Évaluer les valeurs dynamiques avant de les insérer dans le template
    const markerColor = isNewParking ? '#10b981' : '#3b82f6';
    const popupText = isNewParking ? t('parking.dragToAdjust') : t('parking.youAreHere');
    // Préparer des chaînes sûres via JSON.stringify pour éviter toute casse de syntaxe
    const markerHtml = `<div style="background:${markerColor};color:white;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-size:16px;box-shadow:0 2px 8px rgba(0,0,0,0.3);border:3px solid white;">📍</div>`;
    const markerHtmlJSON = JSON.stringify(markerHtml);
    const popupHtmlJSON = JSON.stringify(`<b>${popupText}</b>`);
    const yourCarPopupJSON = JSON.stringify(`<b>${t('parking.yourCar')}</b>`);

    mapHTML += `
      <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
      <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
      <div id="map" style="width:100%;height:100%;"></div>
      <script>
        const map = L.map('map').setView([${center[0]}, ${center[1]}], 15);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap'
        }).addTo(map);

        ${parkingData ? `
          const parkingIcon = L.divIcon({
            html: '<div style="background:#ef4444;color:white;border-radius:50%;width:32px;height:32px;display:flex;align-items:center;justify-content:center;font-size:20px;box-shadow:0 2px 8px rgba(0,0,0,0.3);">🚗</div>',
            iconSize: [32, 32],
            className: ''
          });
          L.marker([${parkingData.lat}, ${parkingData.lng}], {icon: parkingIcon})
            .addTo(map)
            .bindPopup(${yourCarPopupJSON});
        ` : ''}

        ${displayPosition ? `
          const currentIcon = L.divIcon({
            html: ${markerHtmlJSON},
            iconSize: [28, 28],
            className: ''
          });
          const marker = L.marker([${displayPosition.lat}, ${displayPosition.lng}], {icon: currentIcon, draggable: ${isNewParking}})
            .addTo(map)
            .bindPopup(${popupHtmlJSON});
          
          ${isNewParking ? `
            marker.on('dragend', function(event) {
              const position = event.target.getLatLng();
              window.parent.postMessage({
                type: 'markerDragged',
                lat: position.lat,
                lng: position.lng
              }, '*');
            });
          ` : ''}
        ` : ''}

        ${parkingData && displayPosition ? `
          L.polyline([
            [${parkingData.lat}, ${parkingData.lng}],
            [${displayPosition.lat}, ${displayPosition.lng}]
          ], {
            color: '#3b82f6',
            weight: 3,
            opacity: 0.7,
            dashArray: '10, 10'
          }).addTo(map);
          
          map.fitBounds([
            [${parkingData.lat}, ${parkingData.lng}],
            [${displayPosition.lat}, ${displayPosition.lng}]
          ], { padding: [50, 50] });
        ` : ''}
      </script>
    `;

    mapHTML += '</div>';
    
    const blob = new Blob([mapHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    mapRef.current.src = url;
    
    // Attendre que la carte soit chargée
    setTimeout(() => setIsMapLoading(false), 1500);
  }, [parkingData, currentLocation, tempPosition, t]);

  useEffect(() => {
    if (isOpen && mapRef.current && (parkingData || currentLocation || tempPosition)) {
      // Ne recharger la carte que lors de l'ouverture initiale ou changements significatifs
      // (pas à chaque micro-mouvement de currentLocation)
      if (!mapInitializedRef.current || tempPosition || isFullscreen) {
        const timer = setTimeout(() => {
          initMap();
          mapInitializedRef.current = true;
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [isOpen, parkingData, currentLocation, tempPosition, isFullscreen, initMap]);

  // Initialiser la carte lors de la première détection de position
  useEffect(() => {
    if (currentLocation && !parkingData && !mapInitializedRef.current) {
      setIsMapLoading(true);
      const timer = setTimeout(() => {
        if (mapRef.current) {
          initMap();
          mapInitializedRef.current = true;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentLocation, parkingData, initMap]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'markerDragged') {
        setTempPosition({
          lat: event.data.lat,
          lng: event.data.lng
        });
        setIsEditingPosition(true);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (isOpen && !currentLocation && !geoError) {
      requestGeolocation();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);


  const calculateDistance = () => {
    if (!parkingData || !currentLocation) return null;
    
    const R = 6371;
    const dLat = (currentLocation.lat - parkingData.lat) * Math.PI / 180;
    const dLon = (currentLocation.lng - parkingData.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(parkingData.lat * Math.PI / 180) * Math.cos(currentLocation.lat * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c * 1000;
    
    return distance < 1000 
      ? `${Math.round(distance)} m` 
      : `${(distance / 1000).toFixed(2)} km`;
  };

  const saveParking = () => {
    const positionToSave = tempPosition || currentLocation;
    
    if (!positionToSave) {
      window.alert(t('parking.errors.noLocation'));
      return;
    }

    if (navigator.vibrate) {
      navigator.vibrate(200);
    }

    const data = {
      lat: positionToSave.lat,
      lng: positionToSave.lng,
      timestamp: Date.now(),
      notes: notes || '',
      photo: photo || null
    };

    setParkingData(data);
    localStorage.setItem('parkingData', JSON.stringify(data));
    setShowNotes(false);
    setTempPosition(null);
    setIsEditingPosition(false);
    mapInitializedRef.current = false; // Réinitialiser pour recharger la carte avec les deux marqueurs
  };

  const resetPosition = () => {
    setTempPosition(null);
    setIsEditingPosition(false);
  };

  const deleteParking = () => {
    if (window.confirm(t('parking.confirmDelete'))) {
      setParkingData(null);
      setNotes('');
      setPhoto(null);
      setShowNotes(false);
      stopCompass(); // Arrêter la boussole
      mapInitializedRef.current = false; // Réinitialiser pour la prochaine sauvegarde
      localStorage.removeItem('parkingData');
    }
  };

  const openNavigation = () => {
    if (!parkingData) return;
    
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const url = isIOS
      ? `maps://maps.apple.com/?daddr=${parkingData.lat},${parkingData.lng}` 
      : `https://www.google.com/maps/dir/?api=1&destination=${parkingData.lat},${parkingData.lng}`;
    
    window.open(url, '_blank');
  };

  const shareLocation = async () => {
    if (!parkingData) return;

    const text = `${t('parking.shareText')} : https://www.google.com/maps?q=${parkingData.lat},${parkingData.lng}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('parking.shareTitle'),
          text: text
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          copyToClipboard(text);
        }
      }
    } else {
      copyToClipboard(text);
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      window.alert(t('parking.linkCopied'));
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      window.alert(t('parking.linkCopied'));
    }
  };

  const handlePhotoCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhoto(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    stopCompass(); // Arrêter la boussole
    mapInitializedRef.current = false; // Réinitialiser à la fermeture
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <div className={`parking-assistant-overlay ${isClosing ? 'closing' : ''}`}>
      <div className={`parking-assistant-modal ${isFullscreen ? 'fullscreen' : ''} ${isClosing ? 'closing' : ''}`}>
        <div className="parking-header">
          <h2>{parkingData ? t('parking.myParking') : t('parking.saveParking')}</h2>
          <button onClick={handleClose} className="parking-close-btn" aria-label={t('navigation.close')}>
            <X size={24} />
          </button>
        </div>

        <div className="parking-content">
          <div className="parking-local-storage-note">
            <Lock size={18} />
            <span>{t('parking.localStorageNotice')}</span>
          </div>

          {!parkingData ? (
            <div className="parking-save-section">
              {geoError && (
                <div className="parking-error">
                  {geoError}
                  <button onClick={requestGeolocation} className="retry-btn">
                    {t('parking.retry')}
                  </button>
                </div>
              )}

              {!currentLocation && !geoError && (
                <div className="parking-loading">
                  <div className="loading-spinner"></div>
                  <div>{t('parking.geolocating')}</div>
                  <div className="loading-subtext">{t('parking.allowAccess')}</div>
                </div>
              )}

              {currentLocation && (
                <>
                  <div className="parking-info-box">
                    <MapPin size={24} />
                    <div>
                      <strong>{t('parking.positionDetected')}</strong>
                      <p>{isEditingPosition ? t('parking.positionAdjusted') : t('parking.positionReady')}</p>
                    </div>
                  </div>

                  <div className="parking-preview-map-container">
                    {isMapLoading && (
                      <div className="map-loading-overlay">
                        <div className="loading-spinner"></div>
                        <p>{t('parking.geolocating')}</p>
                      </div>
                    )}
                    <iframe
                      ref={mapRef}
                      className="parking-preview-map"
                      title="Parking Preview Map"
                      style={{ opacity: isMapLoading ? 0 : 1 }}
                    />
                    {!isMapLoading && (
                      <div className="map-help-text">
                        {t('parking.dragPinHelp')}
                      </div>
                    )}
                  </div>

                  {isEditingPosition && (
                    <button onClick={resetPosition} className="reset-position-btn">
                      {t('parking.resetPosition')}
                    </button>
                  )}

                  {!showNotes ? (
                    <button onClick={() => setShowNotes(true)} className="add-note-btn">
                      + {t('parking.addNote')}
                    </button>
                  ) : (
                    <div className="note-input-section">
                      <label>{t('parking.noteLabel')}</label>
                      <input
                        type="text"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder={t('parking.notePlaceholder')}
                      />
                    </div>
                  )}

                  <div className="photo-section">
                    <label>{t('parking.photoLabel')}</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handlePhotoCapture}
                      className="photo-input-hidden"
                    />
                    <button onClick={() => fileInputRef.current?.click()} className="photo-btn">
                      <Camera size={20} />
                      {photo ? t('parking.changePhoto') : t('parking.takePhoto')}
                    </button>
                    {photo && (
                      <div className="photo-preview">
                        <img src={photo} alt="Parking" />
                        <button onClick={() => setPhoto(null)} className="photo-delete-btn">
                          <X size={16} />
                        </button>
                      </div>
                    )}
                  </div>

                  <button onClick={saveParking} className="save-parking-btn">
                    {t('parking.savePosition')}
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="parking-view-section">
              {parkingData.notes && (
                <div className="parking-notes-display">
                  <strong>{parkingData.notes}</strong>
                </div>
              )}

              {parkingData.photo && (
                <img src={parkingData.photo} alt="Parking" className="parking-photo-display" />
              )}

              {showCompass && currentLocation && (
                <div className="compass-widget">
                  <div className="compass-header">
                    <div className="compass-title">{t('parking.compass.direction')}</div>
                    <button onClick={stopCompass} className="compass-close-btn" aria-label="Fermer la boussole">
                      <X size={18} />
                    </button>
                  </div>
                  <div 
                    className="compass-arrow"
                    style={{
                      transform: `rotate(${getCompassRotation()}deg)`,
                      transition: 'transform 0.3s ease-out'
                    }}
                  >
                    <div className="arrow-pointer"></div>
                  </div>
                  <div className="compass-bearing">{Math.round(calculateBearing())}°</div>
                  <div className="compass-distance">{calculateDistance()}</div>
                </div>
              )}

              {!showCompass && compassSupported && currentLocation && (
                <button onClick={startCompass} className="enable-compass-btn">
                  <Compass size={20} />
                  {t('parking.compass.enable')}
                </button>
              )}

              <div className="parking-map-container">
                <iframe
                  ref={mapRef}
                  className="parking-map"
                  title="Parking Map"
                />
              </div>

              {calculateDistance() && (
                <div className="distance-display">
                  <div className="distance-value">{calculateDistance()}</div>
                  <div className="distance-label">{t('parking.distanceToCar')}</div>
                </div>
              )}

              <div className="parking-info-row">
                <Clock size={18} />
                <span>{t('parking.parkedSince')} {elapsedTime}</span>
              </div>

              <div className="parking-actions-grid">
                <button onClick={openNavigation} className="action-btn primary">
                  <NavigationIcon size={20} />
                  {t('parking.navigation')}
                </button>
                <button onClick={shareLocation} className="action-btn secondary">
                  <Share2 size={20} />
                  {t('parking.share')}
                </button>
              </div>

              <div className="parking-actions-row">
                <button onClick={() => setIsFullscreen(!isFullscreen)} className="icon-btn">
                  {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                </button>
                <button onClick={deleteParking} className="delete-parking-btn">
                  <Trash2 size={20} />
                  {t('parking.delete')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParkingAssistant;
